import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./lib/i18n";
import CookieConsentBanner from "./components/CookieConsentBanner";
import ConsentScriptLoader from "./components/ConsentScriptLoader";
import { getRouteImporter, prefetchRoute } from "./lib/routePrefetch";

const importIndex = getRouteImporter("/");
const importLegal = getRouteImporter("/impressum");
const importAdmin = getRouteImporter("/admin");
const importNotFound = getRouteImporter("/404");
const importKarriere = getRouteImporter("/karriere");

const Index = lazy(importIndex);
const Legal = lazy(importLegal);
const Admin = lazy(importAdmin);
const NotFound = lazy(importNotFound);
const Karriere = lazy(importKarriere);

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground">
    Laedt...
  </div>
);

const RoutePrefetch = () => {
  useEffect(() => {
    const prefetch = () => {
      prefetchRoute("/karriere");
      prefetchRoute("/impressum");
    };

    if (typeof window === "undefined") {
      return;
    }

    if ("requestIdleCallback" in window) {
      const idleId = (window as Window & {
        requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
        cancelIdleCallback: (id: number) => void;
      }).requestIdleCallback(prefetch, { timeout: 1500 });

      return () =>
        (window as Window & {
          cancelIdleCallback: (id: number) => void;
        }).cancelIdleCallback(idleId);
    }

    const timeoutId = globalThis.setTimeout(prefetch, 1200);
    return () => globalThis.clearTimeout(timeoutId);
  }, []);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <CookieConsentBanner />
        <BrowserRouter>
          <RoutePrefetch />
          <ConsentScriptLoader />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/karriere" element={<Karriere />} />
              <Route path="/impressum" element={<Legal />} />
              <Route path="/datenschutz" element={<Legal />} />
              <Route path="/agb" element={<Legal />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
